// Component: SmartTradeErrorBoundary.jsx
// Purpose: React error boundary for the SmartTrade domain — catches render/lifecycle
//          errors in SmartOrderForm and sibling SmartTrade components and displays a
//          recoverable fallback UI instead of a blank page.
import React from 'react';

class SmartTradeErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
    this.handleReset = this.handleReset.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    //! Critical: log the full stack so we can diagnose SmartTrade render failures
    console.log('[DIAG] SmartTradeErrorBoundary caught error', {
      error: error?.toString(),
      componentStack: errorInfo?.componentStack,
    });
    this.setState({ error, errorInfo });
  }

  handleReset() {
    this.setState({ hasError: false, error: null, errorInfo: null });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full min-h-[200px] p-6 bg-[var(--badge-loss-bg)] rounded-xl border border-[var(--divider)]">
          <div className="text-[var(--badge-loss-fg)] font-black text-base mb-2 uppercase tracking-wide">
            Something went wrong in SmartTrade
          </div>
          <p className="text-xs text-[var(--badge-loss-fg)] text-center mb-4">
            The order form encountered an unexpected error. Your positions are unaffected.
          </p>
          {this.state.error && (
            <pre className="mb-4 p-3 bg-[var(--badge-loss-bg)] text-[var(--badge-loss-fg)] rounded text-[10px] overflow-auto max-h-32 w-full">
              {this.state.error.toString()}
            </pre>
          )}
          <div className="flex gap-2">
            <button
              onClick={this.handleReset}
              className="px-4 py-2 bg-[var(--danger)] hover:opacity-90 text-white text-xs font-bold rounded transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-bg-3 hover:bg-bg-2 text-fg-2 text-xs font-bold rounded transition-colors"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default SmartTradeErrorBoundary;
